import './Viewer.css'
import React, { Suspense, useState, useRef } from 'react'
import createFragment from 'react-addons-create-fragment'
import classnames from 'classnames'
import fromPairs from 'lodash/fromPairs'
import range from 'lodash/range'
import throttle from 'lodash/throttle'
import formatMessage from 'format-message'
import {
  isElementFreetextAnnotation,
  isPenEvent,
  getMostVisiblePage,
  getPageWidthAfterScaling,
} from './utils'
import {
  DEFAULT_AREA_ANNOTATION_COLOR,
  DEFAULT_FREETEXT_ANNOTATION_COLOR,
  DEFAULT_FREETEXT_BACKGROUND_COLOR,
  DEFAULT_HIGHLIGHT_ANNOTATION_COLOR,
  DEFAULT_INK_ANNOTATION_COLOR,
  DEFAULT_INK_STROKE_WIDTH,
  DEFAULT_POINT_ANNOTATION_COLOR,
  DEFAULT_STRIKEOUT_ANNOTATION_COLOR,
  MARGIN_BETWEEN_PAGES,
  DEFAULT_FONT,
} from './constants'
import SpinnerLoading from '../Loading'
import ViewerControls from './ViewerControls'
import Page from './page/Page'
import GenericPopup from './GenericPopup'
import { getAccessTokenFromAppState } from '../utils'
import { generateMergedPdfDownloadUrl } from './annotations/merge-utils'

const ScreenreaderAnnotations = React.lazy(() => import('./annotations/ScreenreaderAnnotations'))

const EMPTY_ARRAY = []
const isMSEdge = /Edge/.test(navigator.userAgent)

class PDFjsViewer_ extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      colors: {
        point: this.props.localStorage.pointColor || DEFAULT_POINT_ANNOTATION_COLOR,
        highlight: this.props.localStorage.highlightColor || DEFAULT_HIGHLIGHT_ANNOTATION_COLOR,
        strikeout: this.props.localStorage.strikeoutColor || DEFAULT_STRIKEOUT_ANNOTATION_COLOR,
        ink: this.props.localStorage.inkColor || DEFAULT_INK_ANNOTATION_COLOR,
        area: this.props.localStorage.areaColor || DEFAULT_AREA_ANNOTATION_COLOR,
        freetext: this.props.localStorage.freetextColor || DEFAULT_FREETEXT_ANNOTATION_COLOR,
      },
      bgColors: {
        freetext: this.props.localStorage.freetextBgColor || DEFAULT_FREETEXT_BACKGROUND_COLOR,
      },
      freetextFont: this.props.localStorage.freetextFont || DEFAULT_FONT,
      currentPageNum: 0,
      hasError: false,
      isCommenting: false,
      displayCommentRegion: false,
      visiblePages: {},
      hasAnyPageLoadedSuccessfully: false,
      isAnnotatable: this.props.session.isAnnotatable,
      inkStrokeWidth: parseInt(this.props.localStorage.inkStrokeWidth) || DEFAULT_INK_STROKE_WIDTH,
      downloadState: 'idle', // 'idle' | 'downloading' | 'success' | 'error'
    }
  }

  handleDisableAnnotations = () => {
    this.props.setControlMode('selection')
    this.setState({ isAnnotatable: false })
  }

  componentDidCatch(error, info) {
    this.setState(
      (state) => ({ ...state, hasError: true }),
      () => {
        console.error('Error caught in PDFjsViewer', { error, info })
      },
    )
  }

  shouldComponentUpdate(_nextProps, _nextState) {
    if (this.state.downloadState !== _nextState.downloadState) return true
    if (
      this.state.hasAnyPageLoadedSuccessfully === false &&
      this.props.document.wasPageLoadedSuccessfully === false
    ) {
      return false
    } else {
      return true
    }
  }

  handleSizeWarningConfirmation = (state) => {
    this.props.setSizeWarningConfirmation(state)
    let div = document.getElementById('sizeWarning')
    div.className = 'file-warn-dialog'
    if (!state) {
      this.props.loadDocument(this.props.session.data.url)
      div.className = 'file-warn-dialog hide'
    }
  }

  toggleSizeWarning = () => {
    this.props.setSizeWarningConfirmation(false)
    this.handleSizeWarningConfirmation(false)
    document.getElementById('loading-container').classList.toggle('hide')
  }

  trapFocusWarningContainer = () => {
    const div = document.getElementById('downloadDocument')
    const focusableEls = div.querySelectorAll('a')
    const firstFocusableEl = focusableEls[0]
    const lastFocusableEl = focusableEls[focusableEls.length - 1]

    div.addEventListener('keydown', function (e) {
      const isTabPressed = e.key === 'Tab'

      if (!isTabPressed) {
        return
      }

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus()
          e.preventDefault()
        }
      } else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus()
          e.preventDefault()
        }
      }
    })
  }

  async componentDidMount() {
    let fileRequest = new XMLHttpRequest()
    fileRequest.open('GET', this.props.session.data.url, true)
    // Note : we can't send an Authorization Bearer token here, because this URL can 302 redirect to S3,
    // and many browsers (Chrome, Edge) pass on Authorization headers even to 3rd party 302 redirects.
    // AWS throws an error if you pass it more than one auth mechanism.  So, our server exchanges
    // this non-standard, non-reserved header for an Authorization Bearer token, allowing S3 redirect to work.
    fileRequest.setRequestHeader('file-download-token', getAccessTokenFromAppState())
    fileRequest.onprogress = (_event) => {
      let fileSize = fileRequest.getResponseHeader('Content-Length') / 1000000

      if (fileSize >= 30) {
        this.handleSizeWarningConfirmation(true)
        this.trapFocusWarningContainer()
        document.getElementById('loading-container').classList.toggle('hide')
      } else {
        this.props.loadDocument(this.props.session.data.url)
        this.props.setSizeWarningConfirmation(false)
      }
      fileRequest.abort()
    }
    fileRequest.send()

    const pagesDiv = document.querySelector('.Pages')
    if (pagesDiv instanceof HTMLElement) {
      this.pagesDiv = pagesDiv
    }
    if (this.props.session.showAnnotations) {
      this.props.loadAnnotations(this.props.session.data.url)
    }
    this.handleWindowResizeThrottled = throttle(this.handleWindowResize, 100, {
      leading: false,
    })

    this.handleAnnotationCommentPresence(this.props.annotations, this.state.isCommenting)

    window.addEventListener('resize', this.handleWindowResizeThrottled)

    this.listenForFirstInteraction()

    /**
     * These event handlers are to prevent accidental scrolling while leaving annotations with a stylus.
     *
     * When a pointerdown event comes in `isPenActive` is set to true if the event came from a pen.
     * Conversely, `isPenActive` is set to false if a pointerup/pointerleave event comes from a pen.
     *
     * Scrolling is prevented by calling event.preventDefault() on touchmove events that come through
     * when `isPenActive` is true and we are not in selection mode.
     */
    this.pagesDiv.addEventListener('touchmove', this.handleTouchMove)
    this.pagesDiv.addEventListener('pointerdown', this.handlePointerDown)
    this.pagesDiv.addEventListener('pointerup', this.handlePointerUp)
    this.pagesDiv.addEventListener('pointerleave', this.handlePointerLeave)
  }

  handleTouchMove = (event) => {
    const shouldBlockTouchMove =
      this.props.isPenActive.current && this.props.controlMode !== 'selection'
    if (shouldBlockTouchMove) {
      event.preventDefault()
    }
  }

  listenForFirstInteraction = () => {
    document.addEventListener('mousedown', this.handleFirstInteraction)
    document.addEventListener('keydown', this.handleFirstInteraction)
    document.addEventListener('touchstart', this.handleFirstInteraction)
    document.addEventListener('pointerdown', this.handleFirstInteraction)
    const pagesDiv = document.querySelector('.Pages')
    if (pagesDiv != null) {
      pagesDiv.addEventListener('scroll', this.handleFirstInteraction)
    }
  }

  stopListeningToFirstInteraction = () => {
    document.removeEventListener('mousedown', this.handleFirstInteraction)
    document.removeEventListener('keydown', this.handleFirstInteraction)
    document.removeEventListener('touchstart', this.handleFirstInteraction)
    document.removeEventListener('pointerdown', this.handleFirstInteraction)
    const pagesDiv = document.querySelector('.Pages')
    pagesDiv.removeEventListener('scroll', this.handleFirstInteraction)
  }

  handleFirstInteraction = () => {
    this.stopListeningToFirstInteraction()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResizeThrottled)
    this.pagesDiv.removeEventListener('touchmove', this.handleTouchMove)
    this.pagesDiv.removeEventListener('pointerdown', this.props.handlePointerDown)
    this.pagesDiv.removeEventListener('pointerup', this.props.handlePointerUp)
    this.pagesDiv.removeEventListener('pointerleave', this.props.handlePointerLeave)
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.hasAnyPageLoadedSuccessfully &&
      !prevState.displayCommentRegion &&
      this.state.displayCommentRegion
    ) {
      this.handleAnnotationCommentPresence(this.props.annotations, this.state.isCommenting)
      this.handleWindowResizeThrottled()
    }
    const didScaleChange =
      this.props.document?.scale && this.props.document.scale !== prevProps.document.scale
    const didFullscreenChange = this.props.isFullscreen !== prevProps.isFullscreen

    if (didFullscreenChange && this.props.isFullscreen === false) {
      this.props.isItSafeToHandleWindowResize.current = false
    }

    if (didScaleChange || didFullscreenChange) {
      this.handleScaledPosition(this.props.document.scale, prevProps.document.scale)
    }

    if (didScaleChange && didFullscreenChange === false) {
      this.props.isItSafeToHandleWindowResize.current = false
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!this.state.hasAnyPageLoadedSuccessfully && this.props.document.wasPageLoadedSuccessfully) {
      this.setState((state) => ({
        ...state,
        hasAnyPageLoadedSuccessfully: true,
      }))
    }
    this.handleAnnotationCommentPresence(nextProps.annotations, this.state.isCommenting)

    if (nextProps.document.numPages !== this.props.document.numPages) {
      const pageRefs = [...Array(nextProps.document.numPages)].map(() => React.createRef())
      this.props.setPageRefs(pageRefs)
    }
  }

  handleScaledPosition = (scale, prevScale) => {
    if (this.pagesDiv instanceof HTMLElement) {
      const scaleChange = scale / prevScale
      const newScrollTop = scaleChange * this.pagesDiv.scrollTop
      this.pagesDiv.scrollTop = newScrollTop

      setTimeout(() => {
        const TOLERANCE = 50
        if (
          newScrollTop < this.pagesDiv.scrollTop - TOLERANCE ||
          newScrollTop > this.pagesDiv.scrollTop + TOLERANCE
        ) {
          this.pagesDiv.scrollTop = newScrollTop
        }
      }, 0)
    }
  }

  handleIsCommenting = ({ isCommenting }) => {
    this.setState((state) => ({ ...state, isCommenting }))
  }

  handleAnnotationCommentPresence = (annotations, isCommenting) => {
    const annotationComments = annotations.data
      ? annotations.data.filter(
          (annotation) => annotation.contents && annotation.type !== 'freetext',
        )
      : EMPTY_ARRAY
    this.setState((state) => ({
      ...state,
      displayCommentRegion: annotationComments.length > 0 || Boolean(isCommenting),
    }))
  }

  handleControlModeChange = (controlMode) => {
    const didSwitchToSelectionMode =
      this.props.controlMode !== controlMode && controlMode === 'selection'
    if (didSwitchToSelectionMode) {
      this.props.selectAnnotation(null)
    }
    this.props.setControlMode(controlMode)
    this.props.setIsPenAnnotating(false)
  }

  handleCurrentColorChange = (currentColor, controlMode) => {
    const colors = {
      ...this.state.colors,
      [this.props.controlMode]: currentColor,
    }
    this.setState({ colors })
    this.props.setIsPenAnnotating(false)
    try {
      this.props.localStorage.setItem(`${controlMode || this.props.controlMode}Color`, currentColor)
    } catch (error) {
      console.info(error)
    }
  }

  handleCurrentBgColorChange = (currentBgColor, controlMode) => {
    const bgColors = {
      ...this.state.bgColors,
      [controlMode || this.props.controlMode]: currentBgColor,
    }
    this.props.setIsPenAnnotating(false)
    this.setState({
      bgColors,
    })
    try {
      this.props.localStorage.setItem(
        `${controlMode || this.props.controlMode}BgColor`,
        currentBgColor,
      )
    } catch (error) {
      console.info(error)
    }
  }

  handleCurrentTextSizeChange = (currentFont, controlMode) => {
    this.props.setIsPenAnnotating(false)
    this.setState(
      {
        freetextFont: currentFont,
      },
      () => {
        try {
          this.props.localStorage.setItem(
            `${controlMode || this.props.controlMode}Font`,
            currentFont,
          )
        } catch (error) {
          console.info(error)
        }
      },
    )
  }

  handleCurrentInkStrokeWidthChange = (currentStrokeWidth, controlMode) => {
    this.props.setIsPenAnnotating(false)
    this.setState(
      {
        inkStrokeWidth: currentStrokeWidth,
      },
      () => {
        try {
          this.props.localStorage.setItem(
            `${controlMode || this.props.controlMode}StrokeWidth`,
            currentStrokeWidth,
          )
        } catch (error) {
          console.info(error)
        }
      },
    )
  }

  handleVisibilityChange = (pageNumber, intersectionRatio) => {
    const visiblePages = Object.assign({}, this.state.visiblePages, {
      [String(pageNumber)]: intersectionRatio,
    })
    this.setState((state) => ({
      ...state,
      visiblePages,
      currentPageNum: getMostVisiblePage(visiblePages),
    }))
  }

  handleWindowResize = (_event) => {
    if (this.props.isFullscreen || this.props.isItSafeToHandleWindowResize.current === false) {
      this.props.isItSafeToHandleWindowResize.current = true
      return
    }

    const viewportWidth = this.props.document.viewport ? this.props.document.viewport.width : 0
    const modifiedPageWidth = getPageWidthAfterScaling(
      this.state.displayCommentRegion,
      viewportWidth,
    )
    if (!(document.body instanceof HTMLElement)) {
      return
    }
    const scrollbarWidth = window.innerWidth - document.body.offsetWidth
    const pageOffsetModifier = 35 * 2 + scrollbarWidth

    const pagesDiv = document.querySelector('#App')
    this.prevScrolltop = pagesDiv.scrollTop
    this.prevScale = this.props.currentPageScale

    this.props.scalePageByRatio(window.innerWidth - pageOffsetModifier, modifiedPageWidth)
  }

  updateViewerPage = (currentPageNum) => {
    this.setState({ currentPageNum }, () => this.focusAndScrollToPage(currentPageNum))
  }

  focusAndScrollToPage = (newPageNum) => {
    const pageDiv = document.querySelector(`#page-${newPageNum}`)
    if (pageDiv instanceof HTMLElement === false) {
      return
    }
    const viewerControls = document.querySelector('.ViewerControls')
    if (viewerControls instanceof HTMLElement) {
      const navHeight = viewerControls.getBoundingClientRect().height
      if (this.pagesDiv instanceof HTMLElement) {
        this.pagesDiv.scrollTop = pageDiv.offsetTop - navHeight - MARGIN_BETWEEN_PAGES
      }
    }
  }

  setDivRef = (node) => {
    if (node instanceof HTMLElement) {
      this.pagesDiv = node
    }
  }

  focusOnPreviousPage = (pageRef) => {
    const index = this.props.pageRefs.indexOf(pageRef)
    const pageFocusIndex = index === 0 ? this.props.pageRefs.length - 1 : index - 1
    const previousPageHasAnnotation =
      this.props.pageRefs[pageFocusIndex].current.querySelector(
        `#page-${pageFocusIndex} .Annotation`,
      ) != null
    if (previousPageHasAnnotation) {
      const previousPageAnnotations = this.props.pageRefs[pageFocusIndex].current.querySelectorAll(
        `#page-${pageFocusIndex} .Annotation`,
      )
      const lastAnnotation = previousPageAnnotations[previousPageAnnotations.length - 1]
      const isFreetextAnnotation = isElementFreetextAnnotation(lastAnnotation)
      if (isFreetextAnnotation) {
        lastAnnotation.querySelector('textarea').focus()
      } else {
        lastAnnotation.querySelector('.Annotation-svg-container').focus()
      }
    }
  }

  focusOnNextPage = (pageRef) => {
    const index = this.props.pageRefs.indexOf(pageRef)
    const pageFocusIndex = index === this.props.pageRefs.length - 1 ? 0 : index + 1
    const nextPageHasAnnotation =
      this.props.pageRefs[pageFocusIndex].current.querySelector(
        `#page-${pageFocusIndex} .Annotation`,
      ) != null
    if (nextPageHasAnnotation) {
      const nextPageAnnotation = this.props.pageRefs[pageFocusIndex].current.querySelector(
        `#page-${pageFocusIndex} .Annotation`,
      )
      const isFreetextAnnotation = isElementFreetextAnnotation(nextPageAnnotation)
      if (isFreetextAnnotation) {
        nextPageAnnotation.querySelector('textarea').focus()
      } else {
        nextPageAnnotation.querySelector('.Annotation-svg-container').focus()
      }
    } else {
      this.props.pageRefs[pageFocusIndex].current.querySelector(`#page-${pageFocusIndex}`).focus()
    }
  }

  handleFocusOut = (event) => {
    const relatedTargetOutsideOfPageContainer =
      this.pagesDiv.contains(event.relatedTarget) === false
    if (relatedTargetOutsideOfPageContainer) {
      this.props.setIsShiftPressed(false)
    }
  }

  handleChangePagesContainerScrollTop = (selectedAnnotation) => {
    const annotationHeight = selectedAnnotation.rect[0][1] - selectedAnnotation.rect[1][1]

    const element = document.querySelector(`#Annotation-${selectedAnnotation.id}`)

    if (element == null) {
      return
    }

    const viewerControlsHeight = document
      .querySelector('.ViewerControls')
      .getBoundingClientRect().height

    const hasSecondaryToolbar = document.querySelector('.SecondaryControls') != null
    const secondaryControlsHeight = hasSecondaryToolbar
      ? document.querySelector('.SecondaryControls').getBoundingClientRect().height
      : 0

    const toolbarsOffset = viewerControlsHeight + secondaryControlsHeight
    const windowHeight = window.innerHeight
    const isFreetextAnnotation = isElementFreetextAnnotation(element)

    let annotationTop
    let annotationBottom
    if (isFreetextAnnotation) {
      const { top } = element.getBoundingClientRect()
      annotationTop = top + this.pagesDiv.scrollTop
      annotationBottom = annotationTop + annotationHeight
    } else {
      const { top } = element.querySelector('.Annotation-svg-container').getBoundingClientRect()
      annotationTop = top + this.pagesDiv.scrollTop
      annotationBottom = annotationTop + annotationHeight
    }

    const annotationIsVisible =
      annotationBottom <= this.pagesDiv.scrollTop + windowHeight &&
      this.pagesDiv.scrollTop + toolbarsOffset <= annotationTop

    if (annotationIsVisible === false) {
      this.pagesDiv.scrollTop = annotationTop - toolbarsOffset
    }
  }

  confirmGraderLimitModal = () => {
    this.props.dismissGraderLimitModal()
    this.handleDisableAnnotations()
  }

  confirmAnnotationErrorModal = () => {
    this.props.dismissAnnotationErrorModal()
  }

  handleIsPenAnnotatingChange = (isPenAnnotating) => {
    this.props.isPenAnnotating !== isPenAnnotating && this.props.setIsPenAnnotating(isPenAnnotating)
  }

  performDownloadWithUrl = async (downloadUrl, filename) => {
    const response = await fetch(downloadUrl, {
      headers: { 'file-download-token': getAccessTokenFromAppState() },
    })

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`)
    }

    const blob = await response.blob()
    const blobURL = window.URL.createObjectURL(blob)

    const downloadLink = document.createElement('a')
    downloadLink.href = blobURL
    downloadLink.download = filename
    downloadLink.style.display = 'none'

    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    window.URL.revokeObjectURL(blobURL)
  }

  downloadDocument = async () => {
    if (this.state.downloadState === 'downloading') return
    const startTime = Date.now()
    const minimumDuration = 800

    try {
      this.setState({ downloadState: 'downloading' })

      let downloadUrl

      if (this.state.isAnnotatable) {
        downloadUrl = await generateMergedPdfDownloadUrl()
      } else {
        downloadUrl = this.props.session.data.url
      }

      await this.performDownloadWithUrl(downloadUrl, this.props.session.data.documentName)

      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, minimumDuration - elapsed)

      setTimeout(() => {
        this.setState({ downloadState: 'success' })
        setTimeout(() => {
          this.setState({ downloadState: 'idle' })
        }, 2000)
      }, remainingTime)
    } catch (error) {
      console.error('Download failed:', error)
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, minimumDuration - elapsed)

      setTimeout(() => {
        this.setState({ downloadState: 'error' })
        setTimeout(() => {
          this.setState({ downloadState: 'idle' })
        }, 2000)
      }, remainingTime)

      if (window.Raven && window.Raven.captureException) {
        window.Raven.captureException(error, {
          tags: {
            component: 'downloadDocument',
            action: 'file_download',
          },
        })
      }
    }
  }

  getDownloadIconClass = () => {
    switch (this.state.downloadState) {
      case 'downloading':
        return 'fa-spinner fa-spin'
      case 'success':
        return 'fa-check'
      case 'error':
        return 'fa-exclamation-triangle'
      default:
        return 'fa-download'
    }
  }

  getDownloadButtonText = () => {
    switch (this.state.downloadState) {
      case 'downloading':
        return formatMessage('Downloading...')
      case 'success':
        return formatMessage('Downloaded!')
      case 'error':
        return formatMessage('Download failed')
      default:
        return formatMessage('Download')
    }
  }

  render() {
    const { documentLoadError, pageLoadError } = this.props.document

    const currentColor =
      this.props.controlMode !== 'selection' ? this.state.colors[this.props.controlMode] : null
    const currentBgColor = this.state.bgColors[this.props.controlMode]
    const sessionUserObject =
      this.props.session.showAnnotations || this.state.isAnnotatable
        ? {
            user_id: this.props.session && this.props.session.userId,
            user_name:
              this.props.session &&
              this.props.session.session &&
              this.props.session.session.annotations &&
              this.props.session.session.annotations.user_name,
            permissions:
              this.props.session &&
              this.props.session.session &&
              this.props.session.session.annotations &&
              this.props.session.session.annotations.permissions,
            anonymous:
              this.props.session &&
              this.props.session.session &&
              this.props.session.session.annotations &&
              this.props.session.session.annotations.anonymous_instructor_annotations,
            user_role:
              this.props.session &&
              this.props.session.session &&
              this.props.session.session.annotations &&
              this.props.session.session.annotations.user_role,
          }
        : {}

    const documentHasAnnotations = this.props.annotations.data.length > 0
    let pages
    const pageNumbers = range(0, this.props.document.numPages)
    if (this.state.hasAnyPageLoadedSuccessfully) {
      pages = pageNumbers.map((pageNumber) => {
        const annotationsByPage = this.props.annotations.stagedByPage[pageNumber] || EMPTY_ARRAY
        const documentViewportHeight = Math.trunc(this.props.document.viewport.height)
        const documentViewportWidth = Math.trunc(this.props.document.viewport.width)
        return (
          <Page
            annotations={annotationsByPage}
            changePagesContainerScrollTop={this.handleChangePagesContainerScrollTop}
            controlMode={this.props.controlMode}
            currentBgColor={currentBgColor}
            currentColor={currentColor}
            currentFont={this.state.freetextFont}
            currentPageNum={this.state.currentPageNum + 1}
            displayCommentRegion={this.state.displayCommentRegion}
            documentHasAnnotations={documentHasAnnotations}
            focusOnNextPage={this.focusOnNextPage}
            focusOnPreviousPage={this.focusOnPreviousPage}
            hoveredAnnotationId={this.props.annotations.hoveredAnnotationId}
            isAnnotatable={
              this.state.isAnnotatable &&
              !this.props.document.clientRotatedPages[pageNumber]
            }
            isCommenting={this.state.isCommenting}
            isPenAnnotating={this.props.isPenAnnotating}
            isShiftPressed={this.props.isShiftPressed}
            key={pageNumber}
            onCreateAnnotation={this.props.createAnnotation}
            onCurrentBgColorChange={this.handleCurrentBgColorChange}
            onCurrentColorChange={this.handleCurrentColorChange}
            onCurrentInkStrokeWidthChange={this.handleCurrentInkStrokeWidthChange}
            onCurrentTextSizeChange={this.handleCurrentTextSizeChange}
            onDeleteAnnotation={this.props.deleteAnnotation}
            onDeselectAnnotation={this.props.deselectAnnotation}
            onHoverAnnotation={this.props.hoverAnnotation}
            onIsCommentingChange={this.handleIsCommenting}
            onIsPenAnnotatingChange={this.handleIsPenAnnotatingChange}
            onPageResize={this.handleWindowResizeThrottled}
            onSelectAnnotation={this.props.selectAnnotation}
            onUpdateAnnotation={this.props.updateAnnotation}
            onVisibilityChange={this.handleVisibilityChange}
            pageNumber={pageNumber}
            pdfDocument={this.props.document.pdfDocument}
            selectedAnnotationId={this.props.annotations.selectedAnnotationId}
            sessionId={this.props.sessionId}
            sessionUserObject={sessionUserObject}
            pageRef={this.props.pageRefs[pageNumber]}
            strokeWidth={this.props.controlMode === 'ink' ? this.state.inkStrokeWidth : 2}
            customPageRotation={this.props.document.pdfRotationsByPage[pageNumber]}
            viewportHeight={documentViewportHeight}
            viewportScale={this.props.document.viewport.scale}
            viewportWidth={documentViewportWidth}
          />
        )
      })
    }

    const pageNumberToAnnotationNumber = fromPairs(
      pageNumbers.map((pageNumber) => {
        const annotationsByPage = this.props.annotations.stagedByPage[pageNumber] || EMPTY_ARRAY
        return [pageNumber, annotationsByPage.length]
      }),
    )

    const isDrawing = this.props.controlMode !== 'selection'

    const isAnnotationSelected = this.props.annotations.selectedAnnotationId != null

    const selectedAnnotation =
      isAnnotationSelected &&
      this.props.annotations.data.find(
        (annotation) => annotation.id === this.props.annotations.selectedAnnotationId,
      )

    const canEdit = Boolean(
      this.state.isAnnotatable &&
        selectedAnnotation != null &&
        sessionUserObject.user_id === selectedAnnotation.user_id &&
        selectedAnnotation.deleted == null,
    )

    const hasSecondaryToolbar = (isAnnotationSelected && canEdit) || isDrawing

    const pagesClasses = classnames('Pages', {
      'Pages-annotatable': this.state.isAnnotatable,
      'Pages-has-secondary-toolbar': hasSecondaryToolbar,
      'Pen-is-annotating':
        (isMSEdge && this.props.controlMode !== 'selection') || this.state.isPenAnnotating,
    })
    return createFragment({
      meta: (
        <div hidden={true} aria-hidden={true}>
          {`Locale: ${this.props.locale}`}
        </div>
      ),
      screenreaderOnly: <h1 className="screenreader-only">{formatMessage('DocViewer')}</h1>,
      viewerControls: (
        <ViewerControls
          showDownload={this.props.session.showAnnotations}
          controlMode={this.props.controlMode}
          currentBgColor={currentBgColor}
          currentColor={currentColor}
          currentFont={this.state.freetextFont}
          currentInkWidth={this.state.inkStrokeWidth}
          currentPageNum={this.state.currentPageNum + 1}
          currentPageScale={this.props.document.scale}
          documentId={this.props.session.documentId}
          generateMergedPdf={this.props.generateMergedPdf}
          isAnnotatable={this.state.isAnnotatable}
          isFullscreen={this.props.isFullscreen}
          mergedPdfStatus={this.props.annotations.mergedPdfStatus}
          numPages={this.props.document.numPages}
          onControlModeChange={this.handleControlModeChange}
          onCurrentBgColorChange={this.handleCurrentBgColorChange}
          onCurrentColorChange={this.handleCurrentColorChange}
          onCurrentInkStrokeWidthChange={this.handleCurrentInkStrokeWidthChange}
          onCurrentTextSizeChange={this.handleCurrentTextSizeChange}
          onPageChange={this.updateViewerPage}
          onPageScale={this.props.scalePage}
          pageNumberToAnnotationNumber={pageNumberToAnnotationNumber}
          selectedAnnotationId={this.props.annotations.selectedAnnotationId}
          rotatePageClockwise={this.props.rotatePageClockwise}
          toggleFullscreen={this.props.toggleFullscreen}
          userRole={sessionUserObject?.user_role}
        />
      ),
      warning: this.props.sizeWarningConfirmation && (
        <div
          className="file-warn-dialog hide"
          id="sizeWarning"
          style={{
            width: `${this.props.viewportWidth}px`,
          }}
        >
          <link
            rel="stylesheet"
            href={'https://cdnjs.cloudflare.com/ajax/' + 'libs/font-awesome/5.15.4/css/all.min.css'}
            integrity={
              'sha512-1ycn6IcaQQ40/MKBW2W4Rhis/DbILU74C1' +
              'vSrLJxCq57o941Ym01SwNsOMqvEBFlcgUa6xLiPY/NS5R+E6ztJQ=='
            }
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
          />
          <span className="text_warning">
            <i className="fas fa-exclamation-circle"></i>
            ‎‎‏‏‎ {formatMessage('Warning')}
          </span>
          <button
            onClick={this.toggleSizeWarning}
            className="btn btn-close btn-file-warn"
            aria-label="Close"
            type="button"
          >
            &times;
            <span className="tooltip">Dismiss</span>
          </button>

          <div id="downloadDocument" className="file-warn-download">
            <div>
              {formatMessage(
                'Large files can crash the browser. It is recommended to download them instead.',
              )}
            </div>
            <button
              type="button"
              className="download"
              id="dialog_download_button_pdf"
              onClick={this.downloadDocument}
              disabled={this.state.downloadState === 'downloading'}
            >
              <i className={`fa ${this.getDownloadIconClass()}`} aria-hidden="true"></i>
              {this.getDownloadButtonText()}
            </button>
            <button type="button" onClick={this.toggleSizeWarning}>
              <i aria-hidden="true" className="fa fa-times-circle"></i> {formatMessage('Dismiss')}
            </button>
          </div>
        </div>
      ),
      pages: (
        <div
          className={pagesClasses}
          ref={this.setDivRef}
          onKeyUp={this.props.handleKeyUp}
          onKeyDown={this.props.handleKeyDown}
          onBlur={this.handleFocusOut}
        >
          <h2 className="screenreader-only">{formatMessage('Pages')}</h2>
          {documentLoadError == null && this.state.hasAnyPageLoadedSuccessfully === false && (
            <SpinnerLoading />
          )}
          {documentLoadError && <div>{formatMessage('Error loading document')}</div>}
          {pageLoadError && <div>{formatMessage('Error loading page')}</div>}
          {pages}
          <Suspense
            fallback={
              <div className="screenreader-only">{formatMessage('Loading annotations')}</div>
            }
          >
            <ScreenreaderAnnotations annotationsByPage={this.props.annotations.stagedByPage} />
          </Suspense>
          {this.props.annotations.annotationForbidden && (
            <GenericPopup
              message={formatMessage(
                'Grader limit has been reached. No further feedback is required.',
              )}
              onConfirm={this.confirmGraderLimitModal}
              severity="alert"
            />
          )}
          {this.props.annotations.annotationError && (
            <GenericPopup
              message={this.props.annotations.alertErrorMessage}
              onRender={this.confirmAnnotationErrorModal}
              severity="alert"
            />
          )}
        </div>
      ),
    })
  }
}

export default function PDFjsViewer(props) {
  const isPenActive = useRef(false)
  const isItSafeToHandleWindowResize = useRef(true)
  const [controlMode, setControlMode] = useState('selection')
  const [pageRefs, setPageRefs] = useState([])
  const [isShiftPressed, setIsShiftPressed] = useState(false)
  const [isPenAnnotating, setIsPenAnnotating] = useState(false)
  const [sizeWarningConfirmation, setSizeWarningConfirmation] = useState(true)

  const handlePointerDown = (event) => {
    if (isPenEvent(event)) {
      isPenActive.current = true
    }
  }

  const handlePointerUp = (event) => {
    if (isPenEvent(event)) {
      isPenActive.current = false
    }
  }

  const handlePointerLeave = (event) => {
    if (isPenEvent(event)) {
      isPenActive.current = false
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Shift') {
      setIsShiftPressed(true)
    }
  }

  const handleKeyUp = (event) => {
    if (event.key === 'Shift') {
      setIsShiftPressed(false)
    }
  }

  return (
    <PDFjsViewer_
      {...props}
      controlMode={controlMode}
      setControlMode={setControlMode}
      pageRefs={pageRefs}
      setPageRefs={setPageRefs}
      isPenActive={isPenActive}
      isItSafeToHandleWindowResize={isItSafeToHandleWindowResize}
      handlePointerDown={handlePointerDown}
      handlePointerUp={handlePointerUp}
      handlePointerLeave={handlePointerLeave}
      isShiftPressed={isShiftPressed}
      setIsShiftPressed={setIsShiftPressed}
      handleKeyDown={handleKeyDown}
      handleKeyUp={handleKeyUp}
      isPenAnnotating={isPenAnnotating}
      setIsPenAnnotating={setIsPenAnnotating}
      sizeWarningConfirmation={sizeWarningConfirmation}
      setSizeWarningConfirmation={setSizeWarningConfirmation}
    />
  )
}
